import * as yup from 'yup';

import { DCCType } from './types';

import { Option } from 'components/shared/ControlledFields/ControlledSelect';

import { convertToNullWhenNaN } from 'utils/validation';

import i18n from 'config/i18n';
import { getLocale } from 'utils/date';

const t = i18n.getFixedT(getLocale(), 'common', 'adminPortal.cla');

export const fixedCheckboxLabels = [
  {
    flag: 'flags.LEAVE_WEEK_HOURS_MAX',
    label: 'leaveMaxBasedWeekHours',
    isInteger: false
  },
  {
    flag: 'leaveHoursBasedOnWork',
    label: 'leaveBasedOnWork',
    isInteger: true
  },
  {
    flag: 'flags.LEAVE_MIN_NEGATIVE',
    label: 'leaveBasedOnWorkNeverMin',
    isInteger: false
  },
  {
    flag: 'leaveHoursBasedOnPayed',
    label: 'leaveBasedOnPayedHours',
    isInteger: true
  },
  {
    flag: 'plbHoursBasedOnPayed',
    label: 'PLBHoursBasedOnPayedHours',
    isInteger: true
  },
  {
    flag: 'leaveHoursZeroHourContract',
    label: 'leaveHoursOnZeroContracts',
    isInteger: true
  },
  {
    flag: 'plbHoursZeroHourContract',
    label: 'PLBHoursOnZeroContracts',
    isInteger: true
  },
  {
    flag: 'flags.LEAVE_HOURS_EQUAL_PERIODS',
    label: 'leaveHoursOnZeroContractsNextYear',
    isInteger: false
  },
  {
    flag: 'flags.HOURS_PLB_NUC_NEXT_YEAR',
    label: 'PLBOnZeroContractsNextYear',
    isInteger: false
  },
  {
    flag: 'flags.FULL_MONTH_OF_LEAVE_WHEN_START_DURING_MONTH',
    label: 'leaveFullMonthOnPartial',
    isInteger: false
  }
];

export const fixedLeaveTransfer = [
  { flag: 'flags.COMP_LEAVE_FIRST', label: 'compentationLeaveFirst' },
  {
    flag: 'flags.COMP_LEAVE_EARMARKED',
    label: 'compentationLeaveEarmarked'
  },
  {
    flag: 'leaveHoursContractFloor',
    label: 'negativeLeaveBalance',
    isInteger: true
  },
  {
    flag: 'flags.LEAVE_4_PLUS_BEFORE_MIN',
    label: 'overLegalLeaveCurrentYear'
  },
  {
    flag: 'flags.AGE_HOURS_AS_LEAVE_OVER',
    label: 'ageHoursOverLegalLeave'
  }
];

export const fixedExtraHourNumberFields = [
  { name: 'extraThreshPeriod', label: 'outgoingPeriodsThreshold' },
  { name: 'extraMarginPeriod', label: 'outgoingPeriodsMargin' },
  { name: 'extraThreshPeriodLast', label: 'lastPeriodsThreshold' },
  { name: 'extraMarginPeriodLast', label: 'lastPeriodsMargin' }
];

export const deviatingReimbAttandanceFields = [
  { name: 'attendanceBaseHours', label: 'baseHours' },
  { name: 'attendanceHolidayAllowanceHours', label: 'holidayAllowanceHours46' },
  { name: 'attendanceWeekendAllowanceHours', label: 'weekendAllowanceHours46' },
  { name: 'attendanceWeekdayAllowanceHours', label: 'weekdayAllowanceHours46' },
  {
    name: 'attendanceHolidayAllowanceAmount',
    label: 'holidayAllowanceAmount45'
  },
  {
    name: 'attendanceWeekendAllowanceAmount',
    label: 'weekendAllowanceAmount45'
  },
  {
    name: 'attendanceWeekdayAllowanceAmount',
    label: 'weekdayAllowanceAmount45'
  }
];

export const deviatingReimbAttandanceFieldsOnCall = [
  { name: 'onCallBaseHours', label: 'baseHours' },
  { name: 'onCallHolidayAllowanceHours', label: 'holidayAllowanceHours44' },
  { name: 'onCallWeekendAllowanceHours', label: 'weekendAllowanceHours44' },
  { name: 'onCallWeekdayAllowanceHours', label: 'weekdayAllowanceHours44' },
  { name: 'onCallHolidayAllowanceAmount', label: 'holidayAllowanceAmount43' },
  { name: 'onCallWeekendAllowanceAmount', label: 'weekendAllowanceAmount43' },
  { name: 'onCallWeekdayAllowanceAmount', label: 'weekdayAllowanceAmount43' }
];

export const schema = (t) => {
  return yup.object({
    code: yup.string(),
    year: yup.number().required(t('year'))
  });
};

const commonNumberValidationSchema = () =>
  yup
    .number()
    .min(0, t('errors.minZero'))
    .nullable()
    .transform(convertToNullWhenNaN)
    .required(t('errors.required'));
const commonBooleanValidationSchema = () => yup.boolean().default(false);

export const generalSchema = (t) =>
  yup.object({
    claId: yup.number().required(t('code')),
    claExternalCode: yup.string(),
    description: yup.string(),
    year: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('errors.year')),
    workHoursBasedWeekdays: commonBooleanValidationSchema(),
    'flags.LEAVE_HOURS_EQUAL_PERIODS': commonBooleanValidationSchema(),
    'flags.PLB_VISIBILITY': commonBooleanValidationSchema(),
    hoursWorkingPerYear: commonNumberValidationSchema(),
    periodHours: commonNumberValidationSchema(),
    hoursWorkingPerWeek: commonNumberValidationSchema(),
    hoursWorkingPerDay: commonNumberValidationSchema(),
    hoursHolidayPerYear: commonNumberValidationSchema(),
    hoursPlbYear: commonNumberValidationSchema(),
    hoursHolidayYearMinimum: commonNumberValidationSchema(),
    plbName: yup
      .string()
      .max(15, t('errors.max15Chars'))
      .required(t('errors.required')),
    plbValidity: yup.string().default(''),
    contractHoursBasedOnWeekdays: yup.boolean()
  });

export const leaveSchema = yup.object({
  balanceBudgetName: yup.string(),
  balanceBudgetMaxGiftHours: yup.number().min(0),
  'flags.LEAVE_WEEK_HOURS_MAX': yup.boolean(),
  'flags.LEAVE_HOURS_BASED_ON_WORK': yup.boolean(),
  'flags.LEAVE_HOURS_CONTRACT_FLOOR': yup.boolean(),
  'flags.LEAVE_HOURS_BASED_ON_PAYED': yup.boolean(),
  'flags.PLB_HOURS_BASED_ON_PAYED': yup.boolean(),
  'flags.LEAVE_HOURS_ZERO_HOUR_CONTRACT': yup.boolean(),
  'flags.PLB_HOURS_ZERO_HOUR_CONTRACT': yup.boolean(),
  'flags.HOURS_LEAVE_NUC_NEXT_YEAR': yup.boolean(),
  'flags.HOURS_PLB_NUC_NEXT_YEAR': yup.boolean(),
  'flags.FULL_MONTH_OF_LEAVE_WHEN_START_DURING_MONTH': yup.boolean(),
  startBeforeDayToReceiveFullMonthOfLeave: yup.number().min(1).max(31),
  endAfterDayToReceiveFullMonthOfLeave: yup.number().min(1).max(31),
  'flags.COMP_LEAVE_FIRST': yup.boolean(),
  'flags.COMP_LEAVE_EARMARKED': yup.boolean(),
  'flags.LEAVE_MIN_NEGATIVE': yup.boolean(),
  'flags.LEAVE_4_PLUS_BEFORE_MIN': yup.boolean(),
  'flags.AGE_HOURS_AS_LEAVE_OVER': yup.boolean(),
  'flags.BLOCK_FESTIVE_DAY_HOUR': yup.boolean(),
  'flags.COMP_LEAVE_EXACT_HOLIDAYS': yup.boolean(),
  hoursJusFestDays: yup.number().min(0),
  'flags.JUS_URC': yup.boolean(),
  'flags.JUS_MIN_MAX': yup.boolean()
});

export const extraHourSchema = yup.object({
  extraThreshPeriod: yup.number().min(0),
  extraMarginPeriod: yup.number().min(0),
  extraThreshPeriodLast: yup.number().min(0),
  extraMarginPeriodLast: yup.number().min(0),
  'flags.EXTRA_HOURS_NOT_ROUNDED': yup.boolean(),
  extraMaxPeriodPaid: yup.number().min(0),
  'flags.PROC_EXTRA_HOURS_NOMINAL': yup.boolean(),
  dccSvc: yup.number().default(10),
  procExtraNomDutyTypeId: yup.number().nullable(),
  capReferenceNetHours: yup.number().min(0).nullable()
});

export const capPlanSchema = yup.object({
  capReferenceNetHours: commonNumberValidationSchema()
});

export const deviatingReimbAttandanceSchema = yup.object({
  attendanceBaseHours: yup.number().min(0),
  attendanceHolidayAllowanceHours: yup.number().min(0),
  attendanceWeekendAllowanceHours: yup.number().min(0),
  attendanceWeekdayAllowanceHours: yup.number().min(0),
  attendanceHolidayAllowanceAmount: yup.number().min(0),
  attendanceWeekendAllowanceAmount: yup.number().min(0),
  attendanceWeekdayAllowanceAmount: yup.number().min(0),
  onCallBaseHours: yup.number().min(0),
  onCallHolidayAllowanceHours: yup.number().min(0),
  onCallWeekendAllowanceHours: yup.number().min(0),
  onCallWeekdayAllowanceHours: yup.number().min(0),
  onCallHolidayAllowanceAmount: yup.number().min(0),
  onCallWeekendAllowanceAmount: yup.number().min(0),
  onCallWeekdayAllowanceAmount: yup.number().min(0)
});

export const createOptions = (dccOptions: DCCType[]): Option[] => {
  return dccOptions.map((dcc) => {
    return {
      value: dcc.dccId,
      label: dcc.description
    };
  });
};
